<script setup lang="ts">
import { REGIONS } from '~~/shared/regions'
import { BASE_TICKET_EUR, EXTRA_ESTABLISHMENT_EUR } from '~~/shared/account-pricing-constants'
import type { EstablishmentSearchResult } from '~~/shared/search-constants'
import { shouldShowHomeDiscover } from '~/utils/home-discover'

const { t, locale } = useI18n()
const ogLocale = useOgLocale()
const config = useRuntimeConfig()

definePageMeta({
  layout: 'default',
})

useSeoMeta({
  title: () => t('pages.home.seo.title'),
  description: () => t('pages.home.seo.description'),
  ogTitle: () => t('pages.home.seo.ogTitle'),
  ogDescription: () => t('pages.home.seo.ogDescription'),
  ogLocale,
  ogImage: '/images/og-default.jpg',
  ogType: 'website',
  twitterCard: 'summary_large_image',
})

// Schema.org WebPage (Organization + WebSite are now site-wide in app.vue)
useSchemaOrg([
  defineWebPage({
    name: () => t('pages.home.seo.title'),
    description: () => t('pages.home.seo.description'),
    url: () => config.public.siteUrl,
    inLanguage: locale.value === 'en' ? 'en-GB' : 'fr-FR',
  }),
])

const localePath = useLocalePath()

const { data: departmentsData } = useLazyAsyncData('home-departments', () =>
  rawFetch<{ data: { code: string; name: string; slug: string; region: string; establishmentCount: number; giteCount?: number }[] }>('/api/departments'),
)

// Comptage gîtes par région (spec switcher) — byRegion alimente l'annuaire.
// L'eyebrow chiffré du hero ne lit plus cette donnée : il compte via
// `/api/search/count` (HeroHomeV3). BLOQUANT en SSR (pas lazy) : les compteurs
// de l'annuaire doivent être connus au premier rendu serveur (pas de flash ni
// mismatch d'hydratation).
const { data: giteCountData } = await useAsyncData('home-gite-count', () =>
  rawFetch<{ data: { total: number; byRegion: { region: string; count: number }[] } }>('/api/gites/count'),
)

// Type actif — un seul état pilote tout le re-render de la home. L'option gîte
// est toujours proposée (alignement /search), indépendamment du stock.
const { type, isAll, isGite } = useHomeType()

// --- Blog teaser ---
const { data: blogTeaserData, status: blogTeaserStatus } = useLazyAsyncData('homepage-blog-teaser', () =>
  rawFetch<{ data: { slug: string; title: string; excerpt: string | null; coverImage: string | null; coverAlt: string | null; accommodationType: 'CDH' | 'GITE' | 'BOTH' }[] }>('/api/blog', {
    query: { limit: 3, hasCover: 'true' },
  }),
)
// Étiquette de type + estompage (spec-4-25-0). Un article `BOTH` n'est JAMAIS
// estompé ; en mode filtré seul le type strict opposé l'est. En mode « Tout » (all),
// aucun estompage → comportement historique préservé. Réactif à `type` (computed).
const blogTeaserArticles = computed(() =>
  (blogTeaserData.value?.data ?? []).map(a => ({
      title: a.title,
      href: `/blog/${a.slug}`,
      image: a.coverImage ?? undefined,
      description: a.excerpt ?? undefined,
      accommodationType: a.accommodationType,
      dimmed:
        (type.value === 'cdh' && a.accommodationType === 'GITE')
        || (type.value === 'gite' && a.accommodationType === 'CDH'),
    })),
)
const isBlogTeaserLoading = computed(() => blogTeaserStatus.value === 'pending')

// --- Hébergements à découvrir (maillage accueil → fiches, 4.113.0) ---
// Chambres d'hôtes uniquement (endpoint `featured` : PUBLISHED avec photos et
// chambres, plus récentes). Rendu en SSR (le serveur attend aussi les données
// « lazy ») pour exposer les liens fiches sans JS. Section masquée en mode Gîtes
// ou si l'API échoue / ne renvoie rien (l'endpoint renvoie `{ data: [] }` en
// cas d'erreur ; `featuredData` reste `null` si l'appel lui-même échoue).
const { data: featuredData } = useLazyAsyncData('home-featured', () =>
  rawFetch<{ data: EstablishmentSearchResult[] }>('/api/establishments/featured', {
    query: { limit: 8 },
  }),
)
const featuredEstablishments = computed(() => featuredData.value?.data ?? [])
const showFeatured = computed(() => shouldShowHomeDiscover(type.value, featuredEstablishments.value.length))
// CTA vers la recherche : conserve le type CDH choisi (vocabulaire /search).
const searchCtaTo = computed(() =>
  type.value === 'cdh'
    ? { path: localePath('/search'), query: { type: 'chambre-hotes' } }
    : localePath('/search'),
)

interface RegionWithSamples {
  slug: string
  name: string
  departmentSamples: string[]
  establishmentCount: number
  cdhCount: number
  giteCount: number
}

const regionsWithSamples = computed<RegionWithSamples[]>(() => {
  const departments = departmentsData.value?.data ?? []
  // Gîtes comptés par NOM de région (le modèle Gite n'a pas de departmentCode).
  const giteByRegion = new Map<string, number>()
  for (const r of giteCountData.value?.data?.byRegion ?? []) {
    giteByRegion.set(r.region, r.count)
  }

  // Garde-fou : un nom de région gîte qui ne correspond à aucune région du
  // référentiel serait compté dans `giteTotal` (option affichée) mais invisible
  // dans l'annuaire. On le signale en dev pour repérer les données à corriger.
  if (import.meta.dev) {
    const knownRegions = new Set(REGIONS.map(r => r.name))
    for (const r of giteCountData.value?.data?.byRegion ?? []) {
      if (!knownRegions.has(r.region)) {
        console.warn(`[home] Région gîte non mappée au référentiel: "${r.region}" (${r.count} gîte(s) invisibles dans l'annuaire)`)
      }
    }
  }

  const merged = REGIONS.map((region) => {
    // Depuis 4.54.4 la réponse inclut les départements gîte-only. En mode
    // « chambres d'hôtes » ils ne doivent pas être nommés comme destinations
    // (le clic mènerait à une liste CDH vide) ; en mode « Tout » ou « Gîtes »
    // ils ont toute leur place. `giteCount` peut manquer sur un payload servi
    // avant déploiement — on ne filtre alors que sur les CDH.
    const regionDepartments = departments
      .filter(d => d.region === region.name
        && (type.value === 'cdh' ? d.establishmentCount > 0 : d.establishmentCount > 0 || (d.giteCount ?? 0) > 0))
      .sort((a, b) => b.establishmentCount - a.establishmentCount)

    const cdhCount = regionDepartments.reduce((sum, d) => sum + d.establishmentCount, 0)
    const giteCount = giteByRegion.get(region.name) ?? 0
    const departmentSamples = regionDepartments.map(d => d.name)

    return {
      slug: region.slug,
      name: region.name,
      departmentSamples,
      establishmentCount: cdhCount + giteCount,
      cdhCount,
      giteCount,
    }
  })

  // Filtrage + retri par densité DU TYPE ACTIF (handoff §3). En mode filtré,
  // une région sans stock du type disparaît ; en « Tout », total > 0 suffit.
  const densityOf = (r: RegionWithSamples) =>
    isGite.value ? r.giteCount : type.value === 'cdh' ? r.cdhCount : r.establishmentCount

  return merged
    .filter(r => densityOf(r) > 0)
    .sort((a, b) => densityOf(b) - densityOf(a))
})
</script>

<template>
  <div>
    <!-- Hero — recherche + accroche -->
    <HoliHeroHomeV3 :regions="regionsWithSamples" />

    <!-- Explore — grille éditoriale Régions × départements-samples -->
    <HoliExploreMapSectionV3 :regions="regionsWithSamples" :type="type" />

    <!-- Réservez en direct, sans commission — argument côté voyageur (4.113.0) -->
    <HoliBookDirectStepsV3 :type="type" />

    <!-- Destinations populaires — maillage accueil → départements (SSR) -->
    <HoliPopularDestinationsV3 :departments="departmentsData?.data ?? []" :type="type" />

    <!-- Hébergements à découvrir — maillage accueil → fiches CDH (SSR) -->
    <section
      v-if="showFeatured"
      id="discover"
      class="py-20 md:py-28 bg-paper-100 text-paper-900 border-y border-paper-300/60"
      aria-labelledby="discover-title"
    >
      <div class="container mx-auto px-4 lg:px-8">
        <header class="max-w-2xl mb-10 md:mb-14">
          <p class="tag text-sage-700 mb-4">
            {{ $t('pages.home.discover.eyebrow') }}
          </p>
          <h2
            id="discover-title"
            class="font-display font-light tracking-[-0.02em] leading-tight text-3xl md:text-5xl text-paper-900 mb-4"
          >
            {{ $t('pages.home.discover.title') }}
          </h2>
          <p class="font-sans text-base md:text-lg text-paper-900/70 leading-relaxed">
            {{ $t('pages.home.discover.subtitle') }}
          </p>
        </header>

        <ul class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 auto-rows-fr">
          <li
            v-for="establishment in featuredEstablishments"
            :key="establishment.id"
            class="h-full"
          >
            <HoliPropertyCardV3 :establishment="establishment" :search-query="{}" />
          </li>
        </ul>

        <div class="text-center mt-10">
          <NuxtLink
            :to="searchCtaTo"
            class="inline-flex items-center gap-2 rounded-lg px-6 h-11 text-sm font-sans font-medium bg-sage-700 text-paper-50 hover:bg-sage-800 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-sage-500 focus:ring-offset-2 focus:ring-offset-paper-100"
          >
            {{ $t('pages.home.discover.cta') }}
            <UIcon name="i-lucide-arrow-right" class="w-4 h-4" aria-hidden="true" />
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- Bandeau signalétique propriétaire (signage only, not acquisition) -->
    <!-- ============================================ -->
    <section
      id="owner-signpost"
      class="py-8 bg-paper-200 border-y border-paper-300/60"
      :aria-label="$t('pages.home.ownerBanner.ariaLabel')"
    >
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 text-center md:text-left">
          <div>
            <h3 class="text-base md:text-lg font-semibold text-paper-900">
              {{ $t(`pages.home.homeType.banner.${type}`) }}
            </h3>
            <p class="text-sm text-paper-900/70 mt-0.5">
              {{ $t('pages.home.ownerBanner.description') }}
            </p>
          </div>
          <UButton
            :to="isGite ? { path: localePath('/auth/register'), query: { owner: 'true' } } : localePath('/inscrire-chambre-hotes')"
            size="md"
            trailing-icon="i-lucide-arrow-right"
            class="shrink-0 text-paper-50 border-0 shadow-sm cursor-pointer"
            :class="isGite ? 'owner-cta-gite' : 'bg-terracotta hover:bg-terracotta/90'"
          >
            {{ $t('pages.home.ownerBanner.cta') }}
          </UButton>
        </div>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- Section 4 : Définition chambre d'hôtes       -->
    <!-- Bloc extractable pour les IA (AEO/GEO)       -->
    <!-- ============================================ -->
    <section id="chambre-dhotes-definition" class="py-20 md:py-28 bg-paper-50">
      <div class="container mx-auto px-4">
        <!-- Mode « Tout » : deux cartes côte à côte, une par type, à parité. -->
        <template v-if="isAll">
          <div class="text-center max-w-3xl mx-auto mb-12">
            <p class="tag text-paper-900 mb-4 justify-center">
              {{ $t('pages.home.homeType.definition.eyebrowAll') }}
            </p>
            <h2 class="font-display font-light text-display-sm md:text-display-md leading-tight tracking-[-0.02em] text-paper-900">
              {{ $t('pages.home.homeType.definition.titleAll') }}
            </h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <!-- Carte CDH (sauge) -->
            <article class="rounded-2xl border hairline p-8" :style="{ backgroundColor: 'var(--color-type-cdh-soft)' }">
              <TypeBadge type="chambre-hotes" class="mb-4" />
              <h3 class="font-display text-2xl font-light text-paper-900 mb-3">
                {{ $t('pages.home.homeType.definition.cdh.title') }}
              </h3>
              <p class="text-paper-900/80 text-base leading-relaxed font-sans mb-5">
                {{ $t('pages.home.homeType.definition.cdh.text') }}
              </p>
              <ul class="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.1em] text-paper-900/60">
                <li>{{ $t('pages.home.homeType.definition.cdh.stat1') }}</li>
                <li>{{ $t('pages.home.homeType.definition.cdh.stat2') }}</li>
                <li>{{ $t('pages.home.homeType.definition.cdh.stat3') }}</li>
              </ul>
            </article>
            <!-- Carte gîte (ocre) -->
            <article class="rounded-2xl border hairline p-8" :style="{ backgroundColor: 'var(--color-type-gite-soft)' }">
              <TypeBadge type="gite" class="mb-4" />
              <h3 class="font-display text-2xl font-light text-paper-900 mb-3">
                {{ $t('pages.home.homeType.definition.gite.title') }}
              </h3>
              <p class="text-paper-900/80 text-base leading-relaxed font-sans mb-5">
                {{ $t('pages.home.homeType.definition.gite.text') }}
              </p>
              <ul class="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.1em] text-paper-900/60">
                <li>{{ $t('pages.home.homeType.definition.gite.stat1') }}</li>
                <li>{{ $t('pages.home.homeType.definition.gite.stat2') }}</li>
                <li>{{ $t('pages.home.homeType.definition.gite.stat3') }}</li>
              </ul>
            </article>
          </div>
        </template>

        <!-- Mode filtré : une seule carte pleine largeur, teintée du type actif. -->
        <template v-else>
          <div class="text-center max-w-3xl mx-auto mb-12">
            <p class="tag mb-4 justify-center" :class="isGite ? 'text-[var(--color-type-gite-ink)]' : 'text-sage-700'">
              {{ $t('pages.home.definition.eyebrow') }}
            </p>
            <h2 class="font-display font-light text-display-sm md:text-display-md leading-tight tracking-[-0.02em] text-paper-900 mb-6">
              {{ $t(`pages.home.homeType.definition.${isGite ? 'gite' : 'cdh'}.title`) }}
            </h2>
            <p class="text-paper-900/80 text-lg leading-relaxed font-sans">
              {{ isGite ? $t('pages.home.homeType.definition.gite.text') : $t('pages.home.definition.text') }}
            </p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto text-center">
            <div
              v-for="n in 3"
              :key="n"
              class="rounded-2xl border hairline p-8"
              :style="{ backgroundColor: isGite ? 'var(--color-type-gite-soft)' : 'var(--color-type-cdh-soft)' }"
            >
              <p class="text-paper-900/80 text-sm font-medium">
                {{ $t(`pages.home.homeType.definition.${isGite ? 'gite' : 'cdh'}.stat${n}`) }}
              </p>
            </div>
          </div>
        </template>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- Section 4b : Blog teaser — articles récents  -->
    <!-- ============================================ -->
    <section v-if="isBlogTeaserLoading || blogTeaserArticles.length > 0" id="blog-teaser" class="py-20 md:py-28 bg-paper-50">
      <div class="container mx-auto px-4">
        <p class="tag text-sage-700 mb-6">
          {{ $t('pages.home.blogTeaser.eyebrow') }}
        </p>

        <div v-if="isBlogTeaserLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="n in 3" :key="n" class="bg-paper-100 rounded-2xl border hairline overflow-hidden">
            <USkeleton class="aspect-[16/9]" />
            <div class="p-4 space-y-3">
              <USkeleton class="h-5 w-3/4" />
              <USkeleton class="h-4 w-full" />
            </div>
          </div>
        </div>

        <ContentRelatedArticles
          v-else
          :items="blogTeaserArticles"
          :title="t('pages.home.blogTeaser.title')"
          compact
        />

        <div class="text-center mt-8">
          <NuxtLink
            :to="localePath('/blog')"
            class="inline-flex items-center gap-2 rounded-lg px-6 h-11 text-sm font-sans font-medium bg-transparent text-sage-700 border border-sage-300 hover:bg-sage-100 hover:border-sage-500 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-sage-500 focus:ring-offset-2 focus:ring-offset-paper-50"
          >
            {{ $t('pages.home.blogTeaser.cta') }}
            <UIcon name="i-lucide-arrow-right" class="w-4 h-4" />
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- Section 4c : Témoignages hébergeurs          -->
    <!-- Placée juste avant le pitch propriétaire :   -->
    <!-- ce sont des pairs qui parlent à des pairs.   -->
    <!-- ============================================ -->
    <HoliTestimonialsSection />

    <!-- ============================================ -->
    <!-- Section 5 : Propriétaire — CTA inscription   -->
    <!-- ============================================ -->
    <section id="owner" class="py-24 md:py-32 bg-paper-900 text-paper-100">
      <div class="max-w-[1240px] mx-auto px-6">
        <div class="text-center max-w-3xl mx-auto mb-16">
          <p class="tag text-paper-100 mb-6 justify-center">
            {{ $t('pages.home.owners.eyebrow') }}
          </p>
          <h2 class="font-display font-light text-[clamp(2.5rem,12vw,4rem)] md:text-display-lg leading-none tracking-tight text-paper-100 mb-6">
            {{ $t('pages.home.owners.title') }}
          </h2>
          <p class="text-lg text-paper-100/70 font-sans leading-relaxed">
            {{ $t('pages.home.owners.subtitle') }}
          </p>
        </div>

        <!-- Facturation par compte : message principal (60 € + 10 €/bien, une échéance) -->
        <div class="max-w-3xl mx-auto mb-12 rounded-2xl bg-paper-800 border hairline p-8 text-center">
          <div class="font-display text-2xl font-light tracking-tight text-paper-100">
            {{ $t('pages.home.owners.account.title') }}
          </div>
          <div class="mt-4 flex items-center justify-center gap-2 flex-wrap">
            <TypeBadge type="chambre-hotes" size="sm" />
            <TypeBadge type="gite" size="sm" />
          </div>
          <p class="mt-5 text-base text-paper-100/80 font-sans leading-relaxed">
            {{ $t('pages.home.owners.account.summary', { base: BASE_TICKET_EUR, extra: EXTRA_ESTABLISHMENT_EUR }) }}
          </p>
          <p class="mt-3 text-sm text-paper-100/60 font-sans">
            {{ $t('pages.home.owners.account.launch') }}
          </p>
        </div>

        <!-- Offre de lancement 2026 : 3 paliers temporels (structure + copy alignée sur /tarifs) -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <!-- Année 1 — mise en avant (terracotta) -->
          <div class="relative flex flex-col rounded-2xl bg-terracotta text-paper-100 p-8 text-left">
            <span class="absolute -top-3 left-1/2 -translate-x-1/2 tag !text-paper-50 bg-sage-600 px-3 py-1 rounded-full whitespace-nowrap shadow-sm">
              {{ $t('pages.home.owners.plans.currentTag') }}
            </span>
            <div class="flex-1">
              <div class="font-display text-2xl font-light tracking-tight">
                {{ $t('pages.tarifs.plans.year1.title') }}
              </div>
              <div class="mt-1 text-sm text-paper-100/85 font-sans">
                {{ $t('pages.tarifs.plans.year1.subtitle') }}
              </div>
              <div class="mt-8">
                <div class="font-display text-5xl md:text-6xl font-light leading-none tracking-tight">
                  {{ $t('pages.tarifs.plans.year1.price') }}
                </div>
                <div class="mt-2 text-sm text-paper-100/85 font-sans">
                  {{ $t('pages.tarifs.plans.year1.priceNote') }}
                </div>
              </div>
              <p class="mt-8 text-base text-paper-100/90 font-sans leading-relaxed">
                {{ $t('pages.tarifs.plans.year1.description') }}
              </p>
            </div>
          </div>

          <!-- Année 2 -->
          <div class="flex flex-col rounded-2xl bg-paper-800 text-paper-100 border hairline p-8 text-left">
            <div class="flex-1">
              <div class="font-display text-2xl font-light tracking-tight">
                {{ $t('pages.tarifs.plans.year2.title') }}
              </div>
              <div class="mt-1 text-sm text-paper-100/75 font-sans">
                {{ $t('pages.tarifs.plans.year2.subtitle') }}
              </div>
              <div class="mt-8">
                <div class="font-display text-5xl md:text-6xl font-light leading-none tracking-tight">
                  {{ $t('pages.tarifs.plans.year2.price') }}
                </div>
                <div class="mt-2 text-sm text-paper-100/75 font-sans">
                  {{ $t('pages.tarifs.plans.year2.priceNote') }}
                </div>
              </div>
              <p class="mt-8 text-base text-paper-100/80 font-sans leading-relaxed">
                {{ $t('pages.tarifs.plans.year2.description') }}
              </p>
            </div>
          </div>

          <!-- Année 3+ -->
          <div class="flex flex-col rounded-2xl bg-paper-800 text-paper-100 border hairline p-8 text-left">
            <div class="flex-1">
              <div class="font-display text-2xl font-light tracking-tight">
                {{ $t('pages.tarifs.plans.year3.title') }}
              </div>
              <div class="mt-1 text-sm text-paper-100/75 font-sans">
                {{ $t('pages.tarifs.plans.year3.subtitle') }}
              </div>
              <div class="mt-8">
                <div class="font-display text-5xl md:text-6xl font-light leading-none tracking-tight">
                  {{ $t('pages.tarifs.plans.year3.price') }}
                </div>
                <div class="mt-2 text-sm text-paper-100/75 font-sans">
                  {{ $t('pages.tarifs.plans.year3.priceNote') }}
                </div>
              </div>
              <p class="mt-8 text-base text-paper-100/80 font-sans leading-relaxed">
                {{ $t('pages.tarifs.plans.year3.description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Message sans risque -->
        <p class="text-sm text-paper-100/75 text-center mt-12">
          {{ $t('pages.home.owners.noRisk') }}
        </p>

        <div class="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <NuxtLink
            :to="localePath('/auth/register')"
            class="inline-flex items-center justify-center rounded-lg px-8 h-14 text-base font-sans font-medium bg-paper-100 text-paper-900 hover:bg-paper-50 transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper-100"
          >
            {{ $t('pages.home.owners.registerCta') }}
          </NuxtLink>
          <NuxtLink
            :to="localePath('/inscrire-chambre-hotes')"
            class="inline-flex items-center justify-center rounded-lg px-8 h-14 text-base font-sans font-medium border hairline text-paper-100 hover:bg-paper-100/10 transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper-100"
          >
            {{ $t('pages.home.owners.discoverCta') }}
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* CTA propriétaire teinté ocre en mode gîte (signal de type, spec 4.7.0). */
.owner-cta-gite {
  background-color: var(--color-type-gite);
}
.owner-cta-gite:hover {
  background-color: color-mix(in oklab, var(--color-type-gite) 88%, black);
}
</style>
